import { Box, Grid, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../_utils/utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';

type ReportBasicInfoProps = {
  rmaNo: string;
  serialNo: string;
  model: string;
  customerName: string;
  originalCustomer: string;
  inspector: string;
  qcInspector: string;
  inspectionDate: string;
  qcDate: string;
  qcCount: number;
  isInspectionSerialChecked: boolean | undefined;
  isQcSerialChecked: boolean | undefined;
};

const SN_MATCH_TEXT = {
  TRUE: 'OK',
  FALSE: '',
};

const ReportBasicInfo: React.FC<ReportBasicInfoProps> = ({
  rmaNo,
  serialNo,
  model,
  customerName,
  originalCustomer,
  inspector,
  qcInspector,
  inspectionDate,
  qcDate,
  qcCount,
  isInspectionSerialChecked,
  isQcSerialChecked,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userData } = useSelector((state: RootState) => state.userInfo);

  const style = {
    item: {
      display: 'flex',
      fontSize: theme.typography.caption.fontSize,
    },
    value: {
      textDecorationLine: 'underline',
      ml: '5px',
      wordBreak: 'break-all',
    },
  };

  const isSerialChecked = (isChecked: boolean | undefined) => {
    if (isChecked === undefined) {
      return '';
    }
    return isChecked ? SN_MATCH_TEXT.TRUE : SN_MATCH_TEXT.FALSE;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      mt="2rem"
      p="5px"
      bgcolor="rgba(0, 0, 0, 0.05)"
    >
      {/* 1行目 */}
      <Grid container spacing={1} pb="10px">
        <Grid size={{ sm: 4, lg: 2 }} sx={style.item}>
          <Typography>{`${t('LBL_RMA_NO')} : `}</Typography>
          <Typography sx={style.value}>{rmaNo}</Typography>
        </Grid>
        <Grid size={{ sm: 4, lg: 2 }} sx={style.item}>
          <Typography>{`${t('LBL_SERIAL_NO')} : `}</Typography>
          <Typography sx={style.value}>{serialNo}</Typography>
        </Grid>
        <Grid size={{ sm: 4, lg: 2 }} sx={style.item}>
          <Typography>{`${t('LBL_MODEL')} : `}</Typography>
          <Typography sx={style.value}>{model}</Typography>
        </Grid>
        <Grid size={{ sm: 6, lg: 3 }} sx={style.item}>
          <Typography>{`${t('LBL_ORIGINAL_CUSTOMER')} : `}</Typography>
          <Typography sx={style.value}>{originalCustomer}</Typography>
        </Grid>
        <Grid size={{ sm: 6, lg: 3 }} sx={style.item}>
          <Typography>{`${t('LBL_CUSTOMER')} : `}</Typography>
          <Typography sx={style.value}>{customerName}</Typography>
        </Grid>
      </Grid>
      {/* 2行目 */}
      <Grid container spacing={1} pb="10px">
        <Grid size={4} sx={style.item}>
          <Typography>{`${t('LBL_REPORT_INSPECTOR')} : `}</Typography>
          <Typography sx={style.value}>{inspector}</Typography>
        </Grid>
        <Grid size={4} sx={style.item}>
          <Typography>{`${t('LBL_REPORT_INSPECTION_DATE')} : `}</Typography>
          <Typography sx={style.value}>
            {formatDate(inspectionDate, userData.dateFormat)}
          </Typography>
        </Grid>
        <Grid size={4} sx={style.item}>
          <Typography>{`${t('LBL_IN_SERIAL_MATCH')} : `}</Typography>
          <Typography sx={style.value}>
            {isSerialChecked(isInspectionSerialChecked)}
          </Typography>
        </Grid>
      </Grid>
      {/* 3行目 */}
      <Grid container spacing={1}>
        <Grid size={4} sx={style.item}>
          <Typography>{`${t('LBL_REPORT_QC_INSPECTOR')} : `}</Typography>
          <Typography sx={style.value}>{qcInspector}</Typography>
        </Grid>
        <Grid size={qcCount !== 0 ? 3.5 : 4} sx={style.item}>
          <Typography>{`${t('LBL_REPORT_QC_DATE')} : `}</Typography>
          <Typography sx={style.value}>
            {formatDate(qcDate, userData.dateFormat)}
          </Typography>
        </Grid>
        <Grid size={qcCount !== 0 ? 2.5 : 4} sx={style.item}>
          <Typography>{`${t('LBL_QC_SERIAL_MATCH')} : `}</Typography>
          <Typography sx={style.value}>
            {qcCount !== 0 && isSerialChecked(isQcSerialChecked)}
          </Typography>
        </Grid>
        {qcCount !== 0 && (
          <Grid size={2} sx={style.item}>
            <React.Fragment>
              <Typography>{`${t('LBL_QC_COUNT')} : `}</Typography>
              <Typography sx={style.value}>
                {qcCount === -1 ? '' : qcCount}
              </Typography>
            </React.Fragment>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ReportBasicInfo;
